/**
 * Campaign Lists API
 * CRUD operations for campaign lists and members
 */
import { createClient } from './supabase';
import type { CampaignList, CampaignListMember, CampaignListWithCounts } from '@lingaai/types';

export interface CampaignListMemberWithNode extends CampaignListMember {
  node: {
    id: string;
    type: string;
    props: Record<string, unknown>;
  } | null;
}

export interface ListDetailsResponse {
  list: CampaignList;
  members: CampaignListMemberWithNode[];
  counts: {
    suggested: number;
    active: number;
    removed: number;
  };
}

/**
 * Get all campaign lists for the current tenant
 */
export async function getCampaignLists(): Promise<CampaignListWithCounts[]> {
  const supabase = createClient();
  const { data, error } = await supabase.from('campaign_list').select('*, members:campaign_list_member(status)').order('created_at', { ascending: false });

  if (error) throw error;

  return (data || []).map((list) => {
    const members = (list.members || []) as Array<{ status: string }>;
    return {
      ...list,
      members: undefined,
      suggested_count: members.filter((m) => m.status === 'suggested').length,
      active_count: members.filter((m) => m.status === 'active').length,
      removed_count: members.filter((m) => m.status === 'removed').length,
      total_count: members.filter((m) => m.status !== 'removed').length,
    } as CampaignListWithCounts;
  });
}

/**
 * Get a single campaign list by ID
 */
export async function getCampaignList(id: string): Promise<CampaignList | null> {
  const supabase = createClient();
  const { data, error } = await supabase.from('campaign_list').select('*').eq('id', id).single();

  if (error) throw error;
  return data;
}

/**
 * Get campaign list with members and counts
 */
export async function getCampaignListDetails(id: string): Promise<ListDetailsResponse> {
  const supabase = createClient();
  const { data: list, error: listError } = await supabase.from('campaign_list').select('*').eq('id', id).single();

  if (listError) throw listError;

  const { data: members, error: membersError } = await supabase.from('campaign_list_member').select('*, node:nodes(id, type, props)').eq('list_id', id).order('added_at', { ascending: false });

  if (membersError) throw membersError;

  const memberList = (members || []) as CampaignListMemberWithNode[];
  const counts = {
    suggested: memberList.filter((m) => m.status === 'suggested').length,
    active: memberList.filter((m) => m.status === 'active').length,
    removed: memberList.filter((m) => m.status === 'removed').length,
  };

  return { list, members: memberList, counts };
}

/**
 * Create a new campaign list
 */
export async function createCampaignList(data: { name: string; description?: string; list_type: 'static' | 'dynamic'; criteria?: Record<string, unknown> }): Promise<CampaignList> {
  const supabase = createClient();
  const { data: list, error } = await supabase.from('campaign_list').insert(data).select().single();

  if (error) throw error;
  return list;
}

/**
 * Update a campaign list
 */
export async function updateCampaignList(id: string, data: Partial<Pick<CampaignList, 'name' | 'description' | 'criteria'>>): Promise<CampaignList> {
  const supabase = createClient();
  const { data: list, error } = await supabase
    .from('campaign_list')
    .update({ ...data, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return list;
}

/**
 * Delete a campaign list
 */
export async function deleteCampaignList(id: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase.from('campaign_list').delete().eq('id', id);

  if (error) throw error;
}

/**
 * Add members to a list
 */
export async function addListMembers(listId: string, nodeIds: string[], status: 'suggested' | 'active' = 'suggested'): Promise<number> {
  const supabase = createClient();
  const members = nodeIds.map((nodeId) => ({
    list_id: listId,
    node_id: nodeId,
    status,
  }));

  const { data, error } = await supabase.from('campaign_list_member').upsert(members, { onConflict: 'list_id,node_id', ignoreDuplicates: false }).select();

  if (error) throw error;
  return data?.length || 0;
}

/**
 * Update member status
 */
export async function updateMemberStatus(listId: string, nodeIds: string[], status: 'suggested' | 'active' | 'removed', removalReason?: string): Promise<number> {
  const supabase = createClient();
  const updateData: Record<string, unknown> = { status };
  if (status === 'removed' && removalReason) {
    updateData.removal_reason = removalReason;
  }

  const { data, error } = await supabase.from('campaign_list_member').update(updateData).eq('list_id', listId).in('node_id', nodeIds).select();

  if (error) throw error;
  return data?.length || 0;
}

/**
 * Remove members from list (hard delete)
 */
export async function removeListMembers(listId: string, nodeIds: string[]): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase.from('campaign_list_member').delete().eq('list_id', listId).in('node_id', nodeIds);

  if (error) throw error;
}
